/* Custom cart drawer — Phase 1
 *
 * - Open/close drawer (overlay click, X click, Escape key, focus trap).
 * - Intercept any product form submit (`form[action$="/cart/add"]`):
 *   POST the cart via Ajax, re-render the drawer's contents via Shopify's
 *   Sections API, and open it.
 * - Quantity +/- and trash on existing line items, with the same
 *   re-render flow on success.
 * - Empty / non-empty footer toggling.
 *
 * No npm dependencies; only fetch + DOM.
 */
(() => {
  if (window.__customCartDrawerInit) return;
  window.__customCartDrawerInit = true;

  const SECTION_SELECTOR = '.custom-cart-drawer';

  function getDrawer() { return document.querySelector(SECTION_SELECTOR); }

  // Parse the section's "Subscription plan IDs per quantity" textarea:
  // "1:12345,2:23456,3:34567" → { '1': '12345', '2': '23456', '3': '34567' }
  // and return the plan ID for a given qty, or '' if not mapped.
  function planIdForQty(qty) {
    const drawer = getDrawer();
    const raw = drawer && drawer.dataset.spPlanMap ? drawer.dataset.spPlanMap : '';
    if (!raw) return '';
    const pairs = raw.split(',').map((s) => s.trim()).filter(Boolean);
    for (const p of pairs) {
      const [q, id] = p.split(':').map((s) => (s || '').trim());
      if (parseInt(q, 10) === qty && id) return id;
    }
    return '';
  }

  function getSectionId() {
    const el = getDrawer();
    if (!el) return null;
    const cls = Array.from(el.classList).find((c) => c.startsWith('custom-cart-drawer-'));
    return cls ? cls.replace('custom-cart-drawer-', '') : null;
  }

  // iOS Safari-safe body scroll lock. `overflow: hidden` on <body> is
  // ignored by iOS Safari for touch scrolling, so we swap in position:
  // fixed and store the pre-lock scroll position on <body> data. On close,
  // we restore and scroll the page back to where the visitor was.
  function lockBodyScroll() {
    if (document.body.dataset.ccdLockY != null) return; // already locked
    const y = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.ccdLockY = String(y);
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }
  function unlockBodyScroll() {
    const raw = document.body.dataset.ccdLockY;
    if (raw == null) return;
    const y = parseInt(raw, 10) || 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    delete document.body.dataset.ccdLockY;
    window.scrollTo(0, y);
  }

  function open() {
    const el = getDrawer();
    if (!el) return;
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-cart-drawer-open');
    lockBodyScroll();
    // Copy the section's --ccd-overlay-alpha onto :root so the body
    // ::before backdrop (which paints the actual dim) reads the user's
    // saved opacity. The drawer host has the var because it's set in
    // the inline style block; :root needs it because the ::before is
    // attached to body.
    const alpha = getComputedStyle(el).getPropertyValue('--ccd-overlay-alpha').trim();
    if (alpha) document.documentElement.style.setProperty('--ccd-overlay-alpha', alpha);
    // Move focus into the drawer for a11y.
    const panel = el.querySelector('.ccd__panel');
    if (panel) panel.focus();
    // Reveal animation: snap progress var to 0, force reflow, then
    // restore the rendered target on the next frame so CSS width/left
    // transitions animate in.
    const bar = el.querySelector('[data-ccd-fs-bar]');
    if (bar) {
      const target = bar.dataset.targetProg || bar.style.getPropertyValue('--ccd-fs-prog') || '0%';
      bar.dataset.targetProg = target;
      bar.style.setProperty('--ccd-fs-prog', '0%');
      void bar.offsetWidth; // reflow
      requestAnimationFrame(() => {
        bar.style.setProperty('--ccd-fs-prog', target);
      });
    }
  }

  function close() {
    const el = getDrawer();
    if (!el) return;
    el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-cart-drawer-open');
    unlockBodyScroll();
  }

  // Update the header cart-count bubble(s) after any cart mutation.
  // The header renders `.custom-header__cart-count` with `.is-empty` when 0;
  // we mirror both so the bubble updates without a full page reload.
  async function syncHeaderCount() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const count = cart.item_count || 0;
      document.querySelectorAll('.custom-header__cart-count').forEach((el) => {
        el.textContent = String(count);
        el.classList.toggle('is-empty', count === 0);
      });
    } catch (_) { /* silent */ }
  }

  async function fetchDrawerSection() {
    const sectionId = getSectionId();
    if (!sectionId) return null;
    // Section ID needs to be the section instance id (e.g. template--XYZ__abc).
    // We expose it via the element classname `custom-cart-drawer-{section_id}`.
    const res = await fetch(`${window.Shopify.routes.root || '/'}?section_id=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    return res.text();
  }

  async function refreshDrawer() {
    // Kick off header sync in parallel — no need to await, but it fires
    // regardless of whether the drawer HTML fetch succeeds.
    syncHeaderCount();
    const html = await fetchDrawerSection();
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(SECTION_SELECTOR);
    const current = getDrawer();
    if (!fresh || !current) return;

    // Body / footer / count get full replacements (re-rendered list).
    const freshBody = fresh.querySelector('[data-ccd-body]');
    const currentBody = current.querySelector('[data-ccd-body]');
    if (freshBody && currentBody) currentBody.replaceWith(freshBody);

    const freshFooter = fresh.querySelector('[data-ccd-footer]');
    const currentFooter = current.querySelector('[data-ccd-footer]');
    if (freshFooter && currentFooter) currentFooter.replaceWith(freshFooter);

    const freshCount = fresh.querySelector('[data-ccd-count]');
    const currentCount = current.querySelector('[data-ccd-count]');
    if (freshCount && currentCount) currentCount.textContent = freshCount.textContent;

    // Mirror header's empty-state class so the title text + bottom border
    // toggle correctly when the cart goes 0 <-> n.
    const freshHeader = fresh.querySelector('.ccd__header');
    const currentHeader = current.querySelector('.ccd__header');
    if (freshHeader && currentHeader) {
      currentHeader.classList.toggle(
        'ccd__header--empty',
        freshHeader.classList.contains('ccd__header--empty')
      );
    }

    // Generic strip syncer for the three top bars between the header
    // and the body. Inserts when fresh has it and current doesn't;
    // removes when only current has it; replaces when both have it
    // (so any settings change in the customizer takes effect).
    function syncStrip(selector) {
      const f = fresh.querySelector(selector);
      const c = current.querySelector(selector);
      if (f && c) {
        c.replaceWith(f);
      } else if (f && !c) {
        const body = current.querySelector('[data-ccd-body]');
        if (body && body.parentNode) body.parentNode.insertBefore(f, body);
      } else if (!f && c) {
        c.remove();
      }
    }
    syncStrip('[data-ccd-sp]');
    syncStrip('[data-ccd-ts]');

    // Shipping bar: update IN PLACE so the CSS width transition on
    // .ccd__fs-fill animates the change smoothly. Progress is driven
    // by --ccd-fs-prog set on .ccd__fs-bar; updating that var makes
    // both fill width and marker left animate together.
    const freshShipping = fresh.querySelector('[data-ccd-shipping]');
    const currentShipping = current.querySelector('[data-ccd-shipping]');
    if (freshShipping && currentShipping) {
      if (freshShipping.hasAttribute('data-ccd-fs-unlocked')) {
        currentShipping.setAttribute('data-ccd-fs-unlocked', '');
      } else {
        currentShipping.removeAttribute('data-ccd-fs-unlocked');
      }
      const freshText = freshShipping.querySelector('[data-ccd-fs-text]');
      const currentText = currentShipping.querySelector('[data-ccd-fs-text]');
      if (freshText && currentText) currentText.innerHTML = freshText.innerHTML;
      const freshBar = freshShipping.querySelector('[data-ccd-fs-bar]');
      const currentBar = currentShipping.querySelector('[data-ccd-fs-bar]');
      if (freshBar && currentBar) {
        const newProg = freshBar.style.getPropertyValue('--ccd-fs-prog') || '0%';
        currentBar.style.setProperty('--ccd-fs-prog', newProg);
        currentBar.dataset.targetProg = newProg;
      }
      // If the marker availability changed, swap it.
      const freshMarker = freshShipping.querySelector('[data-ccd-fs-marker]');
      const currentMarker = currentShipping.querySelector('[data-ccd-fs-marker]');
      if (freshMarker && !currentMarker) {
        currentBar.appendChild(freshMarker.cloneNode(true));
      } else if (!freshMarker && currentMarker) {
        currentMarker.remove();
      }
    } else if (freshShipping && !currentShipping) {
      // Shipping bar appeared (e.g. user enabled it in customizer);
      // insert it before the body.
      const body = current.querySelector('[data-ccd-body]');
      if (body && body.parentNode) body.parentNode.insertBefore(freshShipping, body);
    } else if (!freshShipping && currentShipping) {
      currentShipping.remove();
    }
  }

  // Swap a subscription line's selling plan: remove the existing line,
  // then re-add with the new plan + new quantity. Trying /cart/change.js
  // with selling_plan first was unreliable — Shopify treats different
  // plans as separate lines and doesn't update in place, which combined
  // with the fallback double-counted items.
  async function swapLinePlan(lineKey, variantId, quantity, newPlanId) {
    const drawer = getDrawer();
    if (drawer) drawer.classList.add('is-loading');
    const vidNum = parseInt(variantId, 10);
    const planNum = parseInt(newPlanId, 10);
    const qtyNum = parseInt(quantity, 10);
    if (!vidNum || !planNum || !qtyNum) {
      console.warn('[ccd] swap: bad ids', { variantId, newPlanId, quantity });
      if (drawer) drawer.classList.remove('is-loading');
      return;
    }
    try {
      const remRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
      if (!remRes.ok) {
        const err = await remRes.json().catch(() => ({}));
        console.warn('[ccd] swap: remove failed', remRes.status, err);
      }
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: vidNum, quantity: qtyNum, selling_plan: planNum }],
        }),
      });
      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        console.warn('[ccd] swap: re-add failed', addRes.status, err);
      }
      await refreshDrawer();
    } catch (err) {
      console.warn('[ccd] plan swap failed:', err);
      await refreshDrawer();
    } finally {
      if (drawer) drawer.classList.remove('is-loading');
    }
  }

  async function postToCart(url, body) {
    const drawer = getDrawer();
    if (drawer) drawer.classList.add('is-loading');
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.warn('Cart drawer: cart request failed', json);
      }
      await refreshDrawer();
      return json;
    } finally {
      if (drawer) drawer.classList.remove('is-loading');
    }
  }

  // ---- Public API ----
  window.CustomCartDrawer = {
    open,
    close,
    refresh: refreshDrawer,
  };

  // ---- Intercept Add-to-cart product forms ----
  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    // 1) classic /cart/add action (with optional locale prefix and .js suffix)
    const action = form.getAttribute('action') || '';
    if (/\/cart\/add(\.js)?$/.test(action) || action.indexOf('/cart/add') !== -1) return true;
    // 2) Shopify's product form has data-type="add-to-cart-form"
    if (form.getAttribute('data-type') === 'add-to-cart-form') return true;
    // 3) any form containing a submit button named "add" (Shopify's convention)
    if (form.querySelector('button[name="add"], input[type="submit"][name="add"]')) return true;
    return false;
  }

  // Use CAPTURING phase so we run BEFORE any existing form-level submit
  // handlers (e.g. the buy_now intercept on .cfp__form). That lets us
  // preventDefault the navigation even if another handler also runs.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!isCartAddForm(form)) return;
    // Let buy_now's existing intercept run instead.
    if (form.dataset && form.dataset.submitAction === 'buy_now') return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const formData = new FormData(form);
    const drawer = getDrawer();
    if (drawer) drawer.classList.add('is-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        console.log('[ccd] /cart/add.js →', res.status, json);
        if (!res.ok) {
          const msg = (json && (json.description || json.message)) || `Add to cart failed (${res.status})`;
          // Surface the error next to the button that was clicked so the
          // visitor isn't left staring at an empty cart drawer.
          try {
            const submitBtn = form.querySelector('button[type="submit"], button[name="add"]')
              || document.activeElement;
            let box = form.querySelector('.ccd-form-error') || document.createElement('div');
            box.className = 'ccd-form-error';
            box.textContent = msg;
            box.style.cssText = 'margin-top:8px;padding:10px 12px;background:#fde8e8;color:#7a1a1a;border:1px solid #f5c2c2;border-radius:8px;font-size:13px;line-height:1.4;';
            if (!box.parentNode) {
              (submitBtn && submitBtn.parentNode ? submitBtn.parentNode : form).appendChild(box);
            }
            setTimeout(() => box && box.parentNode && box.parentNode.removeChild(box), 6000);
          } catch (_) { /* best-effort UI */ }
          // Don't open the drawer when the add failed — showing an empty
          // drawer just confuses visitors. Bail out here.
          return null;
        }
        return json;
      })
      .then((json) => { if (json !== null) return refreshDrawer().then(open); })
      .catch((err) => { console.warn('[ccd] add failed:', err); })
      .finally(() => { if (drawer) drawer.classList.remove('is-loading'); });
  }, true /* useCapture */);

  // ---- Drawer interactions (delegated) ----
  document.addEventListener('click', (e) => {
    // Close on overlay or X
    if (e.target.closest('[data-ccd-close]')) { close(); return; }

    // Quantity +/-
    const qtyBtn = e.target.closest('[data-ccd-qty]');
    if (qtyBtn) {
      const item = qtyBtn.closest('[data-ccd-item]');
      if (!item) return;
      const dir = qtyBtn.dataset.ccdQty;
      const input = item.querySelector('[data-ccd-qty-input]');
      const current = parseInt(input.value, 10) || 0;
      const next = dir === 'inc' ? current + 1 : Math.max(0, current - 1);
      const key = item.dataset.lineKey;

      // If this line is a subscription AND the section has a qty→planId
      // map with a DIFFERENT plan for the new qty, swap the plan by
      // removing the line and re-adding with the new plan. /cart/change.js
      // is unreliable for changing selling_plan across shops, remove+add
      // works everywhere.
      const currentPlanId = item.dataset.sellingPlanId || '';
      const variantId = item.dataset.variantId || '';
      const nextPlanId = (currentPlanId && next > 0 && variantId) ? planIdForQty(next) : '';
      const needsSwap = nextPlanId && nextPlanId !== currentPlanId;

      if (needsSwap) {
        swapLinePlan(key, variantId, next, nextPlanId);
      } else {
        postToCart('/cart/change.js', { id: key, quantity: next });
      }
      return;
    }

    // Remove (trash)
    const trash = e.target.closest('[data-ccd-remove]');
    if (trash) {
      const item = trash.closest('[data-ccd-item]');
      if (!item) return;
      const key = item.dataset.lineKey;
      postToCart('/cart/change.js', { id: key, quantity: 0 });
      return;
    }
  });

  // (Manual qty typing removed — the qty input is display-only now;
  //  +/- buttons drive all changes.)

  // Escape to close
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const drawer = getDrawer();
    if (!drawer) return;
    if (drawer.getAttribute('aria-hidden') === 'false') close();
  });

  // Manual openers: any element with [data-ccd-open] (e.g. header cart icon)
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-ccd-open]');
    if (!opener) return;
    e.preventDefault();
    refreshDrawer().then(() => open());
  });
})();

// Define the custom element so the section host has a tag-name with its own
// lifecycle hook if we need to scope future logic to it specifically.
if (!customElements.get('custom-cart-drawer')) {
  customElements.define('custom-cart-drawer', class extends HTMLElement {});
}
